import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Col, Row, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import secureRandomPassword from 'secure-random-password';
import AvatarUpload from 'components/pages/user/profile/AvatarUpload';
import { Eye, EyeSlash } from 'react-bootstrap-icons';
import { useFileUpload } from 'hooks/useFileUpload';
import useUsers from 'hooks/userManagement/useUsers';

const CHARSETS = {
  lower: 'abcdefghijklmnopqrstuvwxyz',
  upper: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  numbers: '0123456789',
  symbols: '!@#$%^&*()_+-=[]{}|;:,.<>?',
};

const EditableInput = ({
  label,
  name,
  value,
  error,
  onChange,
  onSave,
  originalValue,
  isSaving,
  isEditMode,
  disabled,
  type = 'text',
  appendContent,
  autoComplete,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [internalValue, setInternalValue] = useState(value || '');

  useEffect(() => {
    setInternalValue(value || '');
  }, [value]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    onChange({ target: { name, value: newValue } });
  };

  if (!isEditMode || !onSave) {
    return (
      <Form.Group controlId={name}>
        <Form.Label>{label}</Form.Label>
        <div className="position-relative">
          <Form.Control
            type={type}
            name={name}
            value={value || ''}
            onChange={handleChange}
            isInvalid={!!error}
            disabled={disabled}
            className={appendContent ? 'pe-5' : ''}
            autoComplete={autoComplete}
          />
          {appendContent}
        </div>
        <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
      </Form.Group>
    );
  }

  const toggleEdit = async () => {
    if (isEditing) {
      if (internalValue === (originalValue || '')) {
        toast.info('No changes detected', {
          style: {
            backgroundColor: '#0d6efd', // Bootstrap primary blue bg
            color: 'white'               // white text for contrast
          }
        }); setIsEditing(false);
        return;
      }

      try {
        await onSave(name, internalValue);
        toast.success(`${label} updated successfully!`);
        setIsEditing(false);
      } catch (err) {
        toast.error(`Failed to update ${label}: ${err.message}`);
      }
    } else {
      setIsEditing(true);
    }
  };

  return (
    <Form.Group controlId={name}>
      <div
        className="d-flex justify-content-between align-items-center mb-1"
        style={{ userSelect: 'none' }}
      >
        <Form.Label className="mb-0">{label}</Form.Label>
        <span
          role="button"
          tabIndex={0}
          onClick={toggleEdit}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ' ? toggleEdit() : null)}
          style={{
            cursor: isSaving ? 'wait' : 'pointer',
            color: '#0d6efd',
            fontSize: '0.875rem',
            fontWeight: 500,
            opacity: isSaving ? 0.6 : 1,
          }}
        >
          {isSaving ? 'Saving...' : isEditing ? 'Save' : 'Edit'}
        </span>
      </div>

      <div className="position-relative">
        <Form.Control
          type={type}
          name={name}
          value={internalValue}
          isInvalid={!!error}
          disabled={!isEditing || disabled}
          onChange={(e) => setInternalValue(e.target.value)}
          className={appendContent ? 'pe-5' : ''}
          autoComplete={autoComplete}
        />
        {appendContent}
      </div>
      <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
    </Form.Group>
  );
};

const AccountForm = ({
  formData = {},
  errors = {},
  handleInputChange,
  userId,
  patchUserProperties,
  patchProfileProperties,
  refetch,
  isEditMode,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [originalValues, setOriginalValues] = useState({});
  const [savingFields, setSavingFields] = useState({});
  const initialPreview =
    formData.profilePhoto && String(formData.profilePhoto).startsWith('http')
      ? formData.profilePhoto
      : null;
  const [previewPhoto, setPreviewPhoto] = useState(initialPreview);

  const { uploading, error: uploadError, fileId, uploadFile, getFileMetadata } = useFileUpload();
  const { getUserById } = useUsers();

  useEffect(() => {
    return () => {
      if (previewPhoto?.startsWith('blob:')) URL.revokeObjectURL(previewPhoto);
    };
  }, [previewPhoto]);

  // Resolve profilePhoto file ID into public URL for preview
  useEffect(() => {
    let mounted = true;
    const id = formData.profilePhoto;
    if (!id) return;

    if (String(id).startsWith('http')) {
      setPreviewPhoto(id);
      return;
    }

    (async () => {
      try {
        const meta = await getFileMetadata(id);
        if (!mounted) return;
        const publicUrl = meta?.publicUrl || meta?.url || meta?.fileUrl || null;
        if (publicUrl) setPreviewPhoto(publicUrl);
      } catch {
        // ignore errors
      }
    })();

    return () => {
      mounted = false;
    };
  }, [formData.profilePhoto, getFileMetadata]);

  useEffect(() => {
    const fields = ['firstName', 'middleName', 'lastName', 'username', 'email', 'phone'];
    const originals = {};
    fields.forEach((f) => {
      originals[f] = formData[f] || '';
    });
    setOriginalValues(originals);
  }, [formData]);

  const handleFieldSave = async (fieldName, value) => {
    if (!userId || !patchUserProperties) return;

    setSavingFields((prev) => ({ ...prev, [fieldName]: true }));

    try {
      await patchUserProperties(userId, [{ name: fieldName, value }]);
      setOriginalValues((prev) => ({ ...prev, [fieldName]: value }));
      handleInputChange({ target: { name: fieldName, value } });
      if (refetch) await refetch();
    } catch (err) {
      toast.error(`Failed to update ${fieldName}: ${err.message}`);
    } finally {
      setSavingFields((prev) => {
        const updated = { ...prev };
        delete updated[fieldName];
        return updated;
      });
    }
  };

  const generatePassword = () => {
    const pwd =
      secureRandomPassword.randomPassword({ length: 2, characters: CHARSETS.lower }) +
      secureRandomPassword.randomPassword({ length: 2, characters: CHARSETS.upper }) +
      secureRandomPassword.randomPassword({ length: 2, characters: CHARSETS.numbers }) +
      secureRandomPassword.randomPassword({ length: 2, characters: CHARSETS.symbols });

    const shuffled = [...pwd].sort(() => Math.random() - 0.5).join('');

    handleInputChange({ target: { name: 'password', value: shuffled } });
    handleInputChange({ target: { name: 'confirmPassword', value: shuffled } });

    setShowPassword(true);
  };

  const handleCopy = () => {
    if (!formData.password) return;
    navigator.clipboard.writeText(formData.password).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  const passwordAppend = (
    <Button
      variant="link"
      type="button"
      onClick={() => setShowPassword((prev) => !prev)}
      className="position-absolute top-50 end-0 translate-middle-y text-muted p-0 me-1"
      style={{ zIndex: 10 }}
    >
      {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
    </Button>
  );

  return (
    <>
      {/* Profile Photo */}
      <div className="d-flex align-items-center justify-content-center my-4">
        <AvatarUpload
          src={previewPhoto}
          disabled={uploading}
          onChange={async (file) => {
            // Set local preview immediately
            const previewURL = URL.createObjectURL(file);
            setPreviewPhoto(previewURL);

            try {
              const { fileId, file: fileMeta, meta } = await uploadFile(userId, file);
              console.log('[AvatarUpload] File uploaded:', fileId, fileMeta, meta);

              // Update form state with fileId
              handleInputChange({ target: { name: 'profilePhoto', value: fileId } });

              // Use public URL from metadata if available
              const publicUrl =
                meta?.publicUrl || fileMeta?.publicUrl || fileMeta?.url || fileMeta?.fileUrl || null;
              if (publicUrl) setPreviewPhoto(publicUrl);

              if (isEditMode && patchProfileProperties) {
                try {
                  await patchProfileProperties(userId, [{ name: 'profilePhoto', value: fileId }]);
                  toast.success('Profile photo updated successfully!');
                } catch (err) {
                  toast.error('Failed to update profile on server.');
                  console.error(err);
                }

                if (refetch) {
                  await refetch();
                }
              } else {
                toast.info('Profile photo uploaded — it will be attached when you complete sign up.', {
                  style: {
                    backgroundColor: '#0d6efd', 
                    color: 'white'               
                  }
                });
              }
            } catch (err) {
              toast.error('Failed to upload profile photo.');
              console.error(err);
            }
          }}
        />
      </div>

      {uploading && <div className="mb-2 text-info">Uploading photo...</div>}
      {uploadError && <div className="mb-2 text-danger">Upload error: {uploadError}</div>}

      {/* Name Fields */}
      <Row className="g-3 mb-3">
        {['firstName', 'middleName', 'lastName'].map((field) => (
          <Col md={4} key={field}>
            <EditableInput
              label={field.charAt(0).toUpperCase() + field.slice(1)}
              name={field}
              value={formData[field] || ''}
              error={errors[field]}
              onChange={handleInputChange}
              onSave={isEditMode ? handleFieldSave : null}
              originalValue={originalValues[field]}
              isSaving={savingFields[field]}
              isEditMode={isEditMode}
            />
          </Col>
        ))}
      </Row>

      {/* Username + Phone */}
      <Row className="g-3 mb-3">
        <Col md={6}>
          <EditableInput
            label="Username"
            name="username"
            value={formData.username || ''}
            error={errors.username}
            onChange={handleInputChange}
            onSave={null}
            isSaving={false}
            isEditMode={false}
            disabled={isEditMode}
          />
        </Col>

        <Col md={6}>
          <EditableInput
            label="Phone"
            name="phone"
            value={formData.phone || ''}
            error={errors.phone}
            onChange={handleInputChange}
            onSave={isEditMode ? handleFieldSave : null}
            originalValue={originalValues.phone}
            isSaving={savingFields.phone}
            isEditMode={isEditMode}
          />
        </Col>
      </Row>

      {/* Email */}
      <Row className="g-3 mb-3">
        <Col md={12}>
          <EditableInput
            label="Email"
            name="email"
            value={formData.email || ''}
            error={errors.email}
            onChange={handleInputChange}
            isEditMode={false}
            disabled={isEditMode}
          />
        </Col>
      </Row>

      {/* Password - Only create mode */}
      {!isEditMode && (
        <>
          <Row className="g-3 mb-3">
            <Col md={6}>
              <EditableInput
                label="Password*"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password || ''}
                error={errors.password}
                onChange={handleInputChange}
                onSave={null}
                isSaving={false}
                isEditMode={false}
                appendContent={passwordAppend}
                autoComplete="new-password"
              />
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col xs={12}>
              <Button size="sm" variant="outline-success" onClick={generatePassword}>
                Generate Strong Password
              </Button>

              {formData.password && (
                <Button size="sm" variant="link" className="ms-3" onClick={handleCopy}>
                  Copy to Clipboard
                </Button>
              )}

              {copySuccess && <span className="text-success ms-3 fw-semibold">Copied!</span>}
            </Col>
          </Row>

          {formData.password && (
            <div className="alert alert-info py-2 small">
              <strong>Generated Password: </strong> {formData.password}
            </div>
          )}
        </>
      )}
    </>
  );
};

AccountForm.propTypes = {
  formData: PropTypes.object.isRequired,
  errors: PropTypes.object,
  handleInputChange: PropTypes.func.isRequired,
  userId: PropTypes.string,
  patchUserProperties: PropTypes.func,
  patchProfileProperties: PropTypes.func,
  refetch: PropTypes.func,
  isEditMode: PropTypes.bool,
};

export default AccountForm;
